import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ScrollText, Search, X } from 'lucide-react';
import { AUDIT_ACTIONS, AUDIT_ENTITIES, type AuditEntity, type AuditLog } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { auditApi } from '@/lib/api/endpoints';
import { fmtDateTimeSec } from '@/lib/format';
import { describeUserAgent } from '@/lib/userAgent';
import { AuditChanges } from './AuditChanges';
import { ACTION_LABEL, ACTION_VARIANT, ENTITY_LABEL } from './labels';

const ALL = '__all__';
const PAGE_SIZE = 30;

/** Link para o registro afetado, quando ele tem tela própria. */
function entityLink(entity: AuditEntity, id: string | null): string | null {
  if (!id) return null;
  if (entity === 'lead') return `/leads/${id}`;
  if (entity === 'campaign') return `/campanhas/${id}`;
  if (entity === 'call') return `/chamadas/${id}`;
  return null;
}

const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const dayEnd = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const f = {
    action: params.get('action') ?? '',
    entity: params.get('entity') ?? '',
    q: params.get('q') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    page: Number(params.get('page') ?? 1),
  };
  const set = (k: string, v: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === '' || v === ALL) next.delete(k);
    else next.set(k, String(v));
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };
  const hasFilter = !!(f.action || f.entity || f.q || f.from || f.to);

  const query = {
    page: f.page,
    pageSize: PAGE_SIZE,
    action: f.action || undefined,
    entity: f.entity || undefined,
    q: f.q || undefined,
    from: f.from ? dayStart(f.from) : undefined,
    to: f.to ? dayEnd(f.to) : undefined,
  };
  const logs = useQuery({
    queryKey: ['audit', query],
    queryFn: () => auditApi.list(query),
    placeholderData: (p) => p,
    refetchInterval: 30_000,
  });

  return (
    <div>
      <PageHeader
        title="Auditoria"
        description="Quem fez o quê, quando, de onde e o que mudou. Inclui logins, falhas e sessões."
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Ação</Label>
          <Select value={f.action || ALL} onValueChange={(v) => set('action', v)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as ações</SelectItem>
              {AUDIT_ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {ACTION_LABEL[a]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Entidade</Label>
          <Select value={f.entity || ALL} onValueChange={(v) => set('entity', v)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as entidades</SelectItem>
              {AUDIT_ENTITIES.map((e) => (
                <SelectItem key={e} value={e}>
                  {ENTITY_LABEL[e]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="audit-from">
            De
          </Label>
          <Input
            id="audit-from"
            type="date"
            className="w-40"
            value={f.from}
            onChange={(e) => set('from', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="audit-to">
            Até
          </Label>
          <Input
            id="audit-to"
            type="date"
            className="w-40"
            value={f.to}
            onChange={(e) => set('to', e.target.value)}
          />
        </div>
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="audit-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="audit-q"
              placeholder="Usuário, e-mail ou registro afetado"
              className="pl-8"
              defaultValue={f.q}
              onKeyDown={(e) =>
                e.key === 'Enter' && set('q', (e.target as HTMLInputElement).value.trim())
              }
              onBlur={(e) => e.target.value.trim() !== f.q && set('q', e.target.value.trim())}
            />
          </div>
        </div>
        {hasFilter && (
          <Button variant="ghost" onClick={() => setParams({}, { replace: true })}>
            <X /> Limpar
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {logs.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : logs.data?.items.length ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Data / hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Registro</TableHead>
                    <TableHead>Alterações</TableHead>
                    <TableHead>Origem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.data.items.map((r: AuditLog) => {
                    const link = entityLink(r.entity, r.entityId);
                    return (
                      <TableRow key={r.id} className="align-top">
                        <TableCell
                          className="whitespace-nowrap text-xs text-muted-foreground"
                          title={r.requestId ? `Requisição ${r.requestId}` : undefined}
                        >
                          {fmtDateTimeSec(r.createdAt)}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">
                            {r.actorName ?? <span className="text-muted-foreground">—</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{r.actorEmail ?? ''}</p>
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            label={ACTION_LABEL[r.action] ?? r.action}
                            className={ACTION_VARIANT[r.action] ?? ''}
                          />
                        </TableCell>
                        <TableCell>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {ENTITY_LABEL[r.entity] ?? r.entity}
                          </p>
                          {link ? (
                            <Link to={link} className="text-sm hover:underline">
                              {r.entityLabel ?? r.entityId}
                            </Link>
                          ) : (
                            <p className="text-sm">{r.entityLabel ?? r.entityId ?? '—'}</p>
                          )}
                        </TableCell>
                        <TableCell className="max-w-md whitespace-normal break-words">
                          <AuditChanges changes={r.changes} />
                        </TableCell>
                        <TableCell
                          className="whitespace-nowrap text-xs text-muted-foreground"
                          title={r.userAgent ?? undefined}
                        >
                          <p className="font-mono">{r.ip ?? '—'}</p>
                          <p>{describeUserAgent(r.userAgent)}</p>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <DataTablePagination
              page={logs.data.page}
              pageSize={logs.data.pageSize}
              total={logs.data.total}
              onPageChange={(p) => set('page', p)}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={ScrollText}
              title="Nenhum registro"
              description={
                hasFilter
                  ? 'Nenhuma ação corresponde aos filtros.'
                  : 'As ações aparecem aqui assim que acontecerem.'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
